import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/core/services/app.service';
import { FocuslinkService } from 'src/app/core/services/focuslink.service';
import { ImageCropperService } from 'src/app/core/services/image-cropper.service';
import { LocalDataStoreService } from 'src/app/core/services/local-data-store.service';
import { UserService } from 'src/app/core/services/user.service';
import { OOVenueService } from 'src/app/core/services/venue-service';
import { MenuObjectReorderRequest } from 'src/app/shared/models/DTO/MenuObjectReorderRequest.dto';
import { AppMessageSeverity } from 'src/app/shared/models/enums/AppMessageSeverity.enum';
import { OOCanvas } from 'src/app/shared/models/oo-canvas';
import { OOMenuItem } from 'src/app/shared/models/oo-menu-item';
import { CanvasRecordSetupComponent } from './canvas-record-setup/canvas-record-setup.component';

@Component({
	selector: 'app-canvas-setup',
	templateUrl: './canvas-setup.component.html',
	styleUrls: ['../../commandcenter.scss'],
})
export class CanvasSetupComponent implements OnInit, OnDestroy {
	@ViewChild('canvasRecordSetup') canvasRecordSetup: CanvasRecordSetupComponent;
	// canvasList: OOCanvas[];
	canvasListOnlineOnly: OOCanvas[];
	canvasListAll: OOCanvas[];
	canvasListDisplayed: OOCanvas[];
	canvasSelected: OOCanvas;
	changesToSave: boolean = false;
	canvasViewSelection: string = 'online';
	canvasViewOffline: boolean = false;

	canvasSubscription = new Subscription();

	duplicateCanvasKey: number;

	imageCanvasKey: number;
	imageKeySelected: string;

	loaded: boolean = false;

	moment: any = moment;
	multiSortMeta = [
		{ field: 'online', order: -1 },
		{ field: 'sortOrder', order: 1 },
	];

	newRecord: boolean = false;

	timestamp;
	timezones: any[] = [];

	venueSubscription = new Subscription();

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		private localStorage: LocalDataStoreService,
		private spinner: NgxSpinnerService,
		private userService: UserService,
		public imgCrop: ImageCropperService,
		public venueService: OOVenueService,
		private confirmationService: ConfirmationService,
		private router: Router
	) {}
	ngOnDestroy(): void {
		this.canvasSubscription.unsubscribe();
		this.venueSubscription.unsubscribe();
	}

	ngOnInit(): void {
		this.venueSubscription = this.venueService.subscriptionVenue.subscribe((data) => {
			if (data && data.key) {
				console.log(this.venueService.subscriptionOOcanvas.value);
				if (this.venueService.ooCanvases.length === 0) {
					this.app.loading(true);
					this.venueService.canvasesGet();
				}
			}
		});

		this.timezones = moment.tz.zonesForCountry('US').map((tz) => ({
			label: tz,
			value: tz,
		}));

		if (this.venueService.subscriptionOOcanvas?.value.length === 0) {
			this.venueService.canvasesGet();
		}

		this.canvasSubscription.add(
			this.venueService.subscriptionOOcanvas.subscribe((data) => {
				if (data?.length > 0) {
					this.canvasListAll = [...data];
					this.canvasViewOfflineChange();
				}
			})
		);
	}

	canvasSelect(canvasKey) {
		const existing = this.canvasListDisplayed.find((i) => i.canvasKey === canvasKey);
		if (!existing) {
			this.canvasViewOfflineChange();
		}
		this.router.navigate(['/cc/menu/canvas/', canvasKey]);
	}

	canvasRemoveFromList(canvasKey: number) {
		this.venueService.ooCanvases.splice(
			this.venueService.ooCanvases.findIndex((i) => i.canvasKey === canvasKey),
			1
		);
	}

	canvasViewOfflineChange() {
		if (this.canvasViewOffline) {
			this.canvasListDisplayed = [...this.canvasListAll];
		} else {
			this.canvasListDisplayed = [...this.canvasListAll.filter((i) => i.online)];
		}
		this.venueService.sortForDisplay(this.canvasListDisplayed);
		this.canvasListDisplayed = [...this.canvasListDisplayed];
	}

	async menuItemListGet(venueKey) {
		const resp = await this.focuslink.menuItemGet(venueKey);
		this.venueService.ooMenuItems = resp.map((i) => {
			return new OOMenuItem(i);
		});
	}

	async onCanvasOnlineToggle(event, canvasKey) {
		const idx = this.venueService.ooCanvases.findIndex((i) => i.canvasKey === canvasKey);
		this.venueService.ooCanvases[idx].online = event.checked;
		await this.focuslink.canvasPost(this.venueService.venueKey, this.venueService.ooCanvases[idx]);
	}

	async onCanvasPropertyChange(canvas: OOCanvas) {
		this.venueService.canvaseSave(canvas);
	}

	async onCanvasReorder() {
		this.app.loading(true);
		try {
			this.canvasListOnlineOnly.forEach((canvas, idx) => {
				canvas.sortOrder = idx + 1;
			});

			const sortPayload = this.canvasListOnlineOnly.map((i) => new MenuObjectReorderRequest({ key: i.canvasKey, sortOrder: i.sortOrder }));

			await this.focuslink.ooMenuReorderObject(this.venueService.venueKey, 'can', sortPayload);

			this.venueService.ooCanvases.forEach((can) => {
				const sortPayloadItem = sortPayload.find((i) => i.key === can.canvasKey);
				can.sortOrder = sortPayloadItem ? sortPayloadItem.sortOrder : undefined;
			});

			this.venueService.sortForDisplay(this.venueService.ooCanvases);
			this.canvasViewOfflineChange();
			this.app.loading(false);
			this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `Canvases resorted` });
		} catch (error) {
			this.app.loading(false);
			console.log(error);
			this.app.msgSvc.add({ severity: AppMessageSeverity.RED, summary: '', detail: `Error resorting canvases. Sorry.` });
		}
	}
	async prepareCanvasSort() {
		this.canvasListOnlineOnly = this.venueService.ooCanvases.filter((i) => i.online).sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));
	}

	async selectCanvasImage(canvasKey) {
		this.imageKeySelected = `canvas-${canvasKey}`;
		this.imageCanvasKey = canvasKey;
		this.imgCrop.openImageCropper('canvas', canvasKey, this.venueService.venueKey);
	}

	async sortCanvasView() {
		this.canvasListDisplayed.forEach((i) => {
			if (!i.sortOrder) {
				i.sortOrder = 999999;
			}
		});
		this.canvasListDisplayed.sort((a, b) => {
			if (a.sortOrder && b.sortOrder) {
				return a.sortOrder > b.sortOrder ? 1 : -1;
			} else {
				return a.name > b.name ? 1 : -1;
			}
		});
	}

	async onImageComplete() {
		await this.app.references.delay(1000);
		const c = this.venueService.ooCanvases.find((i) => i.canvasKey === this.imageCanvasKey);
		c.imageKey = `${this.imageKeySelected}.png`;
	}
}
